import { PRIVATE_TODO_TITLE_KEY } from '@/common/constants/constants';
import {
    Planner,
    PlannerWithDetails,
    PlannerWithInfos,
} from '@/common/types/databaseTypes';
import { getEndOfDay, getEndOfMonth, getStartOfDay } from '@tickitacka/shared';
import { authService } from '@/features/auth/services/authService';
import { workspaceService } from '@/features/workspace/services/workspaceService';
import { supabase } from '@tickitacka/shared';
import { useAuthStore } from '@/stores/authStore';
import { useCategoryStore } from '@/stores/categoryStore';
import { v4 as uuidv4 } from 'uuid';

export interface CreatePlannerInput {
    todo_id: string;
    user_id: string;
    register_id: string;
    selected_date: Date;
    blocks: number[];
}

export interface CreateTodoWithPlannerInput {
    todoInput: {
        goal_id: string | null;
        title: string;
        register_id: string;
        user_id: string;
        category_id?: string;
        is_public?: boolean;
        content?: string;
    };
    plannerInput: {
        blocks: number[];
        selected_date: Date;
        register_id: string;
        user_id: string;
    };
}

const convertToPlannerData: (
    input: CreatePlannerInput
) => Omit<Planner, 'id'> = (input: CreatePlannerInput) => {
    const plannerDate = getStartOfDay(input.selected_date);

    return {
        todo_id: input.todo_id,
        user_id: input.user_id,
        register_id: input.register_id,
        planner_date: plannerDate,
        blocks: input.blocks,
        register_date: new Date().toISOString(),
        update_date: new Date().toISOString(),
        update_id: input.register_id,
        enable: true,
    };
};

// 안전하게 count 값을 추출하는 유틸리티 함수
export function extractCount(value: unknown): number {
    // 객체인지 확인
    if (value && typeof value === 'object') {
        // count 속성이 있는지 확인
        const obj = value as Record<string, unknown>;
        if ('count' in obj && typeof obj.count === 'number') {
            return obj.count;
        }

        // count 속성이 문자열인 경우 변환 시도
        if ('count' in obj && typeof obj.count === 'string') {
            const num = Number(obj.count);
            return isNaN(num) ? 0 : num;
        }
    }

    // 값이 직접 숫자나 숫자 문자열인 경우
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        const num = Number(value);
        return isNaN(num) ? 0 : num;
    }

    return 0;
}

export const plannerService = {
    async getPlannerById(plannerId: string): Promise<Planner> {
        try {
            const { data, error } = await supabase
                .from('planner')
                .select('*')
                .eq('id', plannerId)
                .eq('enable', true)
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error(`Failed to fetch planner ${plannerId}`, error);
            throw error;
        }
    },

    async getPlannersByTodoId(todoId: string): Promise<Planner[]> {
        try {
            const { data, error } = await supabase
                .from('planner')
                .select('*')
                .eq('todo_id', todoId)
                .eq('enable', true);

            if (error) throw error;
            return data as Planner[];
        } catch (error) {
            console.error(`Failed to fetch planners for todo ${todoId}`, error);
            throw error;
        }
    },

    async getPlannersByDate(
        date: Date,
        userId: string
    ): Promise<PlannerWithDetails[]> {
        const startDate = getStartOfDay(date);
        const endDate = getEndOfDay(date);

        try {
            const { data, error } = await supabase
                .from('planner')
                .select(
                    `
                        *,
                        todo:todo_id(*),
                        category:todo_id(
                            category:category_id(
                                id,
                                name,
                                color,
                                color_name
                            )
                        ),
                        goal:todo_id(
                            goal:goal_id(
                                id,
                                title,
                                status_code,
                                priority_code,
                                indicator_type,
                                current_value,
                                target_value,
                                register_date,
                                start_date,
                                end_date,
                                team_id
                            )
                        ),
                        project:todo_id(
                            goal:goal_id(
                                project:project_id(
                                    id,
                                    name
                                )
                            )
                        ),
                        leader:todo_id(
                            goal:goal_id(
                                leader:leader_id(
                                    id,
                                    name,
                                    profile_image_url
                                )
                            )
                        ),
                        comments:todo_id(
                            comments_count:comment!todo_id(id, enable)
                        ),
                        notes:todo_id(
                            notes_count:note!todo_id(id, enable)
                        )
                    `
                )
                .gte('planner_date', startDate)
                .lte('planner_date', endDate)
                .eq('user_id', userId)
                .eq('todo.enable', true)
                .eq('enable', true);
            if (error) throw error;

            const transformedData = (data || []).map(planner => {
                // 카테고리 값이 없으면 기본 카테고리 지정
                let category = planner.category?.category;
                if (!category) {
                    const categories = useCategoryStore.getState().categories;
                    if (categories && categories.length > 0) {
                        category = categories[0];
                    }
                }

                const user = useAuthStore.getState().user;
                if (
                    planner.todo?.is_public === false &&
                    user?.id !== planner.todo?.user_id
                ) {
                    planner.todo.title = PRIVATE_TODO_TITLE_KEY;
                }

                const transformed: PlannerWithDetails = {
                    ...planner,
                    category,
                    goal: planner.goal?.goal,
                    project: planner.project?.goal?.project,
                    leader: planner.leader?.goal?.leader,
                    comments_count: Array.isArray(
                        planner.comments?.comments_count
                    )
                        ? planner.comments.comments_count.filter(
                              (comment: { id: string; enable: boolean }) =>
                                  comment?.enable === true
                          ).length
                        : 0,
                    notes_count: Array.isArray(planner.notes?.notes_count)
                        ? planner.notes.notes_count.filter(
                              (note: { id: string; enable: boolean }) =>
                                  note?.enable === true
                          ).length
                        : 0,
                };
                return transformed;
            });

            return transformedData
                .filter(e => e.todo)
                .sort((a, b) => {
                    // blocks가 비어있는 것이 가장 먼저
                    if (!a.blocks.length) return -1;
                    if (!b.blocks.length) return 1;
                    return a.blocks[0] - b.blocks[0];
                });
        } catch (error) {
            console.error(
                `Failed to fetch planners for date ${startDate}`,
                error
            );
            throw error;
        }
    },

    async getUnfinishedPlanners(
        userId: string,
        targetDate: Date
    ): Promise<PlannerWithDetails[]> {
        try {
            const targetISOString = getStartOfDay(targetDate);

            // 1. 해당 날짜 이후에 계획된 todo_id 목록 조회
            const { data: todayPlannedTodos, error: todayError } =
                await supabase
                    .from('planner')
                    .select('todo_id')
                    .eq('user_id', userId)
                    .eq('enable', true)
                    .gte('planner_date', targetISOString);

            if (todayError) throw todayError;

            // 해당 날짜 이후에 계획된 todo_id 목록
            const todayTodoIds = (todayPlannedTodos || []).map(p => p.todo_id);

            // 2. 과거에 계획되었고 완료/취소되지 않은 플래너를 가져오되,
            // 해당 날짜 이후에 다시 계획된 todo는 제외
            const { data, error } = await supabase
                .from('todo')
                .select(
                    `
                    *,
                    planner!todo_id(*),
                    category:category_id(
                        id,
                        name,
                        color,
                        color_name
                    ),
                    goal:goal_id(
                        id,
                        title,
                        status_code,
                        priority_code,
                        indicator_type,
                        current_value,
                        target_value,
                        register_date,
                        start_date,
                        end_date,
                        team_id,
                        project:project_id(
                            id,
                            name
                        ),
                        leader:leader_id(
                            id,
                            name,
                            profile_image_url
                        )
                    ),
                    comments_count:comment!todo_id(id, enable),
                    notes_count:note!todo_id(id, enable)
                `
                )
                .eq('enable', true)
                .eq('user_id', userId)
                .is('complete_date', null)
                .is('cancelled_date', null)
                .lt('planner.planner_date', targetISOString)
                .eq('planner.user_id', userId)
                .eq('planner.enable', true)
                .not(
                    todayTodoIds.length > 0 ? 'id' : 'id',
                    todayTodoIds.length > 0 ? 'in' : 'is',
                    todayTodoIds.length > 0
                        ? `(${todayTodoIds.join(',')})`
                        : null
                );

            if (error) throw error;

            const transformedDataPromises = (data || []).map(async todo => {
                const planner = todo.planner?.[0];
                if (!planner) return null;

                // 워크스페이스 권한 체크
                if (todo.goal?.team_id) {
                    const editPermission =
                        await workspaceService.checkUserWorkspaceMembership(
                            todo.goal?.team_id,
                            userId
                        );

                    if (!editPermission?.isAllowed) {
                        return null;
                    }
                }

                let category = todo.category;
                if (!category) {
                    const categories = useCategoryStore.getState().categories;
                    if (categories && categories.length > 0) {
                        category = categories[0];
                    }
                }

                const user = useAuthStore.getState().user;
                if (
                    todo &&
                    todo.is_public === false &&
                    user?.id !== todo.user_id
                ) {
                    todo.title = PRIVATE_TODO_TITLE_KEY;
                }

                const transformed: PlannerWithDetails = {
                    ...planner,
                    todo,
                    category,
                    goal: todo.goal,
                    project: todo.goal?.project,
                    leader: todo.goal?.leader,
                    comments_count: todo.comments_count?.filter(
                        comment => comment.enable === true
                    ).length,
                    notes_count: todo.notes_count?.filter(
                        note => note.enable === true
                    ).length,
                };
                return transformed;
            });

            // 모든 Promise가 해결된 후 null 값을 필터링
            const resolvedData = await Promise.all(transformedDataPromises);
            return resolvedData.filter(
                (item): item is PlannerWithDetails => item !== null
            );
        } catch (error) {
            console.error('Failed to fetch unfinished planners', error);
            throw error;
        }
    },

    // async getPlannersByDateRange(
    //     startDate: string,
    //     endDate: string,
    //     userId: string
    // ): Promise<PlannerWithDetails[]> {
    //     try {
    //         const { data, error } = await supabase
    //             .from('planner')
    //             .select(
    //                 `
    //                     *,
    //                     todo:todo_id(*),
    //                     category:todo_id(
    //                         category:category_id(
    //                             id,
    //                             name,
    //                             color,
    //                             color_name
    //                         )
    //                     ),
    //                     goal:todo_id(
    //                         goal:goal_id(
    //                             id,
    //                             title,
    //                             status_code,
    //                             priority_code,
    //                             indicator_type,
    //                             current_value,
    //                             target_value,
    //                             register_date,
    //                             start_date,
    //                             end_date,
    //                             team_id
    //                         )
    //                     ),
    //                     project:todo_id(
    //                         goal:goal_id(
    //                             project:project_id(
    //                                 id,
    //                                 name
    //                             )
    //                         )
    //                     ),
    //                     leader:todo_id(
    //                         goal:goal_id(
    //                             leader:leader_id(
    //                                 id,
    //                                 name,
    //                                 profile_image_url
    //                             )
    //                         )
    //                     ),
    //                     comments:todo_id(
    //                         comments_count:comment!todo_id(count)
    //                     ),
    //                     notes:todo_id(
    //                         notes_count:note!todo_id(count)
    //                     )
    //                 `
    //             )
    //             .eq('user_id', userId)
    //             .gte('planner_date', startDate)
    //             .lte('planner_date', endDate)
    //             .eq('enable', true);
    //         // .neq('todo.is_hidden', true);
    //         if (error) throw error;

    //         // 데이터 변환
    //         const transformedData = (data || []).map(planner => {
    //             // 카테고리 값이 없으면 기본 카테고리 지정
    //             let category = planner.category?.category;
    //             if (!category) {
    //                 const categories = useCategoryStore.getState().categories;
    //                 if (categories && categories.length > 0) {
    //                     category = categories[0];
    //                 }
    //             }

    //             const user = useAuthStore.getState().user;
    //             if (
    //                 planner.todo &&
    //                 planner.todo.is_public === false &&
    //                 user?.id !== planner.todo.user_id
    //             ) {
    //                 planner.todo.title = PRIVATE_TODO_TITLE;
    //             }

    //             const transformed: PlannerWithDetails = {
    //                 ...planner,
    //                 category,
    //                 goal: planner.goal?.goal,
    //                 project: planner.project?.goal?.project,
    //                 leader: planner.leader?.goal?.leader,
    //                 comments_count: planner.comments?.comments_count?.[0]
    //                     ? extractCount(planner.comments?.comments_count[0])
    //                     : 0,
    //                 notes_count: planner.notes?.notes_count?.[0]
    //                     ? extractCount(planner.notes?.notes_count[0])
    //                     : 0,
    //             };

    //             return transformed;
    //         });

    //         return transformedData
    //             .filter(e => e.todo)
    //             .sort((a, b) => {
    //                 // blocks가 비어있는 것이 가장 먼저
    //                 if (!a.blocks.length) return -1;
    //                 if (!b.blocks.length) return 1;
    //                 return a.blocks[0] - b.blocks[0];
    //             });
    //     } catch (error) {
    //         console.error('Failed to fetch planners by date range', error);
    //         throw error;
    //     }
    // },

    async getUpcomingPlanners(
        userId: string,
        selectedDate: Date
    ): Promise<PlannerWithInfos[]> {
        const user = useAuthStore.getState().user;

        const startDate = getStartOfDay(selectedDate);
        // 그 달의 마지막 날을 계산
        const endDate = getEndOfMonth(selectedDate).toISOString();

        const { data, error } = await supabase
            .from('planner')
            .select(
                `
                *,
                todo:todo!todo_id(
                    id,
                    title,
                    is_public,
                    user_id,
                    category:category!category_id(
                        id,
                        name,
                        color,
                        color_name
                    )
                )
            `
            )
            .eq('user_id', userId)
            .gte('planner_date', startDate)
            .lte('planner_date', endDate)
            .eq('enable', true)
            .order('blocks', { ascending: true });

        if (error) {
            console.error(error);
            throw error;
        }

        return data.map(planner => {
            if (
                planner.todo &&
                planner.todo.is_public === false &&
                user?.id !== planner.todo.user_id
            ) {
                planner.todo.title = PRIVATE_TODO_TITLE_KEY;
            }

            return {
                ...planner,
                todo: planner.todo,
                category: planner.todo?.category,
            };
        });
    },

    async getPaginatedUpcomingPlanners({
        userId,
        pageParam,
        pageSize,
    }: {
        userId: string;
        pageParam: number;
        pageSize: number;
    }): Promise<PlannerWithInfos[]> {
        const user = useAuthStore.getState().user;
        const startIndex = (pageParam - 1) * pageSize;
        const endIndex = pageParam * pageSize - 1;

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const selectedDate = getStartOfDay(tomorrow);

        const { data: dateRangePlanners, error } = await supabase
            .from('planner')
            .select(
                `
                    *,
                    todo:todo!todo_id(
                        id,
                        title,
                        is_public,
                        user_id,
                        category:category!category_id(
                            id,
                            name,
                            color,
                            color_name
                        )
                    )
                `
            )
            .eq('user_id', userId)
            .gte('planner_date', selectedDate)
            .eq('enable', true)
            .order('planner_date', { ascending: true })
            .order('blocks', { ascending: true })
            .range(startIndex, endIndex);

        if (error) throw error;

        const planners = dateRangePlanners.map(planner => {
            if (
                planner.todo &&
                planner.todo.is_public === false &&
                user?.id !== planner.todo.user_id
            ) {
                planner.todo.title = PRIVATE_TODO_TITLE_KEY;
            }

            return {
                ...planner,
                todo: planner.todo,
                category: planner.todo?.category,
            };
        });

        return planners;
    },

    async createPlanner(input: CreatePlannerInput): Promise<Planner> {
        const newPlanner: Planner = {
            ...convertToPlannerData(input),
            id: uuidv4(),
        };
        try {
            const { data, error } = await supabase
                .from('planner')
                .insert(newPlanner)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Failed to create planner', error);
            throw error;
        }
    },

    async updatePlanner(updatedPlanner: Partial<Planner>): Promise<Planner> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('planner')
                .update({
                    ...updatedPlanner,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', updatedPlanner.id)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Failed to update planner', error);
            throw error;
        }
    },

    async deletePlanner(plannerId: string): Promise<void> {
        try {
            const { error } = await supabase
                .from('planner')
                .delete()
                .eq('id', plannerId);

            if (error) throw error;
            return;
        } catch (error) {
            console.error(`Failed to delete planner ${plannerId}`, error);
            throw error;
        }
    },

    /**
     * Todo 생성과 Planner 생성을 단일 트랜잭션으로 처리하는 함수
     * 기존의 3단계 과정(Todo 생성 -> Planner 생성 -> Todo 업데이트)을 하나의 트랜잭션으로 처리
     */
    async createTodoWithPlanner(
        input: CreateTodoWithPlannerInput
    ): Promise<Planner> {
        try {
            // supabase 트랜잭션 시작
            const { data, error } = await supabase.rpc(
                'create_todo_with_planner',
                {
                    p_todo_title: input.todoInput.title,
                    p_todo_goal_id: input.todoInput.goal_id,
                    p_todo_register_id: input.todoInput.register_id,
                    p_todo_user_id: input.todoInput.user_id,
                    p_todo_category_id: input.todoInput.category_id || null,
                    p_todo_is_public: input.todoInput.is_public ?? true,
                    p_todo_content: input.todoInput.content || null,
                    p_planner_blocks: input.plannerInput.blocks,
                    p_planner_date: getStartOfDay(
                        input.plannerInput.selected_date
                    ),
                    p_planner_register_id: input.plannerInput.register_id,
                    p_planner_user_id: input.plannerInput.user_id,
                }
            );

            if (error) throw error;

            return data[0];
        } catch (error) {
            console.error(
                'Failed to create todo with planner in transaction',
                error
            );
            throw error;
        }
    },
};
